// Swiper Settings (Animation Settings -> "Swiper Settings") -- pure model.
//
// swiperSettingsSchema.json is a byte-for-byte mirror of
// server_elakay/elakay_helpers/swiperSettingsSchema.json (the backend test
// test/swiperSettings.test.js fails if they drift). It lists:
//   - the option catalogue (groups / fields / bounds),
//   - every Swiper component really used by Elakay, with its React "origin"
//     behaviour per device, its usages and its restrictions,
//   - the components deliberately NOT managed, with the reason.
//
// Precedence, property by property and device by device:
//   1. the instance customization of a page (swiperOptions prop, or the
//      legacy wm0..wm1200 props a page already passes),
//   2. the default saved in Swiper Settings,
//   3. the origin behaviour of the React component.
// A page that customizes only the scrollbar colour still inherits every
// other property from the defaults.
import SCHEMA from "./swiperSettingsSchema.json";

export { SCHEMA };
export const DEVICES = Object.freeze(Object.keys(SCHEMA.devices));
export const GROUPS = SCHEMA.groups;
export const COMPONENTS = SCHEMA.components;
export const EXCLUDED_COMPONENTS = SCHEMA.excluded;
export const COMPONENT_BY_ID = Object.freeze(Object.fromEntries(COMPONENTS.map((component) => [component.id, component])));
const FIELDS = Object.freeze(Object.fromEntries(GROUPS.map((group) => [
  group.key,
  Object.freeze(Object.fromEntries(group.fields.map((field) => [field.key, field]))),
])));
export const getField = (groupKey, fieldKey) => FIELDS[groupKey]?.[fieldKey] || null;

// Marker: "use the component's own fine-grained responsive table"
// (its historical 568/768/1024/1200 px breakpoints for slidesPerView /
// spaceBetween). Never stored, never sent to the API.
export const LEGACY = "__legacy__";

const isPlainObject = (value) => Boolean(value) && typeof value === "object" && !Array.isArray(value);

// ── Sanitizing (mirror of elakay_helpers/swiperSettingsRules.js) ──────────
const COLOR_PATTERN = /^(?:#[0-9a-f]{3,8}|rgba?\(\s*[\d.]+%?\s*,\s*[\d.]+%?\s*,\s*[\d.]+%?(?:\s*,\s*[\d.]+%?)?\s*\)|hsla?\(\s*-?[\d.]+(?:deg)?\s*,\s*[\d.]+%\s*,\s*[\d.]+%(?:\s*,\s*[\d.]+%?)?\s*\)|[a-z]{3,30})$/i;
const FORBIDDEN_COLOR_WORDS = /^(?:inherit|initial|unset|revert|expression)$/i;

const roundToStep = (value, step) => {
  if (!step || step >= 1) return Math.round(value / (step || 1)) * (step || 1);
  const decimals = String(step).split(".")[1]?.length || 0;
  return Number((Math.round(value / step) * step).toFixed(decimals));
};

const clampNumber = (value, field) => {
  if (value === "" || value === null || value === undefined || typeof value === "boolean") return undefined;
  const number = Number(value);
  if (!Number.isFinite(number)) return undefined;
  return roundToStep(Math.min(field.max, Math.max(field.min, number)), field.step);
};

export const isValidColor = (value) => typeof value === "string"
  && value.trim().length > 0 && value.trim().length <= 64
  && !FORBIDDEN_COLOR_WORDS.test(value.trim()) && COLOR_PATTERN.test(value.trim());

export const sanitizeValue = (field, value) => {
  if (!field) return undefined;
  switch (field.type) {
    case "boolean": return typeof value === "boolean" ? value : undefined;
    case "booleanAuto": return typeof value === "boolean" || value === "auto" ? value : undefined;
    case "enum": return field.options.includes(value) ? value : undefined;
    case "number": return clampNumber(value, field);
    case "slidesPerView": return value === "auto" ? "auto" : clampNumber(value, field);
    case "color": return isValidColor(value) ? value.trim() : undefined;
    default: return undefined;
  }
};

export const sanitizeDeviceConfig = (input, component = null) => {
  const out = {};
  if (!isPlainObject(input)) return out;
  const unsupported = new Set(component?.unsupportedGroups || []);
  Object.entries(input).forEach(([groupKey, groupValue]) => {
    if (!FIELDS[groupKey] || !isPlainObject(groupValue) || unsupported.has(groupKey)) return;
    const group = {};
    Object.entries(groupValue).forEach(([fieldKey, value]) => {
      let clean = sanitizeValue(FIELDS[groupKey][fieldKey], value);
      if (clean !== undefined && groupKey === "effect" && fieldKey === "type"
        && Array.isArray(component?.allowedEffects) && !component.allowedEffects.includes(clean)) clean = undefined;
      if (clean !== undefined) group[fieldKey] = clean;
    });
    if (Object.keys(group).length) out[groupKey] = group;
  });
  return out;
};

export const sanitizePerDevice = (input, component = null) => {
  const out = {};
  if (!isPlainObject(input)) return out;
  DEVICES.forEach((device) => {
    const clean = sanitizeDeviceConfig(input[device], component);
    if (Object.keys(clean).length) out[device] = clean;
  });
  return out;
};

// ── Devices (same breakpoints as the Page Builder device grid:
//    mobile <= 480 px, tablet 481-768 px, desktop >= 769 px) ─────────────
export const deviceForWidth = (width) => {
  const w = Number(width) || 0;
  if (w <= SCHEMA.devices.mobile.maxWidth) return "mobile";
  if (w <= SCHEMA.devices.tablet.maxWidth) return "tablet";
  return "desktop";
};

// ── Resolution ────────────────────────────────────────────────────────────
// Origin of one device = origin.all deep-merged with origin[device].
export const originFor = (component, device) => {
  // swiperPageMatrix is the common fallback requested by the marketplace.
  // Component origins remain the section-specific layer above it; persisted
  // defaults and instance values are still merged later with higher priority.
  const reference = COMPONENT_BY_ID.pageMatrix?.origin || {};
  const origin = component?.origin || {};
  const out = {};
  [reference.all, reference[device], origin.all, origin[device]].forEach((layer) => {
    if (!isPlainObject(layer)) return;
    Object.entries(layer).forEach(([groupKey, group]) => {
      out[groupKey] = { ...(out[groupKey] || {}), ...group };
    });
  });
  // Visual carousel pagination is now opt-in. This changes the origin/default
  // only: the saved component layer and the instance layer are merged later,
  // so an administrator's explicit true/false remains authoritative.
  out.pagination = { ...(out.pagination || {}), enabled: false };
  return out;
};

// Per-property merge. Returns the effective values AND where each one came
// from ("instance" | "defaults" | "origin"), which the builder needs to
// keep a component's legacy responsive table only while nobody overrode it.
export const resolveDeviceConfig = ({ origin = {}, defaults = {}, instance = {} } = {}) => {
  const config = {};
  const sources = {};
  const layers = [["origin", origin], ["defaults", defaults], ["instance", instance]];
  GROUPS.forEach(({ key: groupKey }) => {
    layers.forEach(([source, layer]) => {
      const group = layer?.[groupKey];
      if (!isPlainObject(group)) return;
      Object.entries(group).forEach(([fieldKey, value]) => {
        if (value === undefined || value === null) return;
        if (!config[groupKey]) { config[groupKey] = {}; sources[groupKey] = {}; }
        config[groupKey][fieldKey] = value;
        sources[groupKey][fieldKey] = source;
      });
    });
  });
  return { config, sources };
};

const get = (config, groupKey, fieldKey, fallback) => {
  const value = config?.[groupKey]?.[fieldKey];
  return value === undefined || value === null ? fallback : value;
};

// ── Builder: resolved config -> <Swiper> props / modules / style ────────
const SINGLE_SLIDE_EFFECTS = new Set(["fade", "cube", "flip", "cards", "creative"]);

const CREATIVE_PRESETS = {
  slide: { prev: { translate: ["-120%", 0, -500] }, next: { translate: ["120%", 0, -500] } },
  perspective: { prev: { shadow: true, translate: ["-20%", 0, -1] }, next: { translate: ["100%", 0, 0] } },
  depth: { prev: { shadow: true, translate: [0, 0, -400] }, next: { translate: ["100%", 0, 0] } },
  flip3d: { prev: { shadow: true, translate: ["-125%", 0, -800], rotate: [0, 0, -90] }, next: { shadow: true, translate: ["125%", 0, -800], rotate: [0, 0, 90] } },
  zoom: { prev: { shadow: true, origin: "left center", translate: ["-5%", 0, -200], rotate: [0, 100, 0] }, next: { origin: "right center", translate: ["5%", 0, -200], rotate: [0, -100, 0] } },
};

const SHADOWS = {
  none: "none",
  sm: "0 1px 3px rgba(15,23,42,0.18)",
  md: "0 4px 14px rgba(15,23,42,0.2)",
  lg: "0 10px 30px rgba(15,23,42,0.28)",
};

const px = (value) => `${value}px`;

// Legacy responsive table -> Swiper `breakpoints`, keeping only what still
// comes from it (slidesPerView and/or spaceBetween).
const legacyBreakpoints = (table, { slidesPerView, spaceBetween }) => {
  if (!isPlainObject(table)) return null;
  const out = {};
  Object.entries(table).forEach(([width, entry]) => {
    if (Number(width) <= 0) return;
    const next = {};
    next.slidesPerView = slidesPerView === LEGACY ? entry.slidesPerView : slidesPerView;
    next.spaceBetween = spaceBetween === LEGACY ? entry.spaceBetween : spaceBetween;
    out[width] = next;
  });
  return Object.keys(out).length ? out : null;
};

/**
 * @param {object} resolved  output of resolveDeviceConfig
 * @param {object} ctx
 *   modules       { Navigation, Pagination, ... } from "swiper/modules"
 *   device        "desktop" | "tablet" | "mobile"
 *   legacyTable   { 0: {slidesPerView, spaceBetween}, 568: {...}, ... } (optional)
 *   autoNavigation value used for navigation.enabled === "auto"
 *   slideCount    number of slides (loop needs enough of them)
 *   preview       true inside the Swiper Settings preview (no window breakpoints)
 */
export const buildSwiperProps = ({ config }, ctx = {}) => {
  const { modules: M = {}, device = "desktop", legacyTable = null, autoNavigation = true, slideCount = Infinity, preview = false } = ctx;
  const props = {};
  const modules = [];
  const style = {};
  const classes = ["elk-swp"];
  const cssVars = {};
  const use = (moduleRef) => { if (moduleRef && !modules.includes(moduleRef)) modules.push(moduleRef); };
  const has = (groupKey, fieldKey) => config?.[groupKey]?.[fieldKey] !== undefined;

  // Effect
  const effect = get(config, "effect", "type", "slide");
  const singleSlide = SINGLE_SLIDE_EFFECTS.has(effect);
  props.effect = effect;
  props.speed = get(config, "effect", "speed", 300);
  const direction = get(config, "effect", "direction", "horizontal");
  props.direction = direction;
  const gridRows = get(config, "layout", "gridRows", 1);
  const loopRequested = get(config, "effect", "loop", false) === true;
  // Loop needs more slides than are visible; Grid and loop are incompatible.
  props.loop = loopRequested && gridRows <= 1 && slideCount > 1;
  props.rewind = !props.loop && get(config, "effect", "rewind", false) === true;
  props.centeredSlides = get(config, "effect", "centeredSlides", false) === true;
  props.grabCursor = get(config, "effect", "grabCursor", false) === true;
  const slideShadows = get(config, "effect", "slideShadows", true) === true;
  if (effect === "fade") { use(M.EffectFade); props.fadeEffect = { crossFade: get(config, "effect", "fadeCrossFade", true) === true }; }
  if (effect === "cube") { use(M.EffectCube); props.cubeEffect = { shadow: get(config, "effect", "cubeShadow", true) === true, slideShadows, shadowOffset: 20, shadowScale: 0.94 }; }
  if (effect === "flip") { use(M.EffectFlip); props.flipEffect = { slideShadows, limitRotation: true }; }
  if (effect === "coverflow") {
    use(M.EffectCoverflow);
    props.coverflowEffect = {
      rotate: get(config, "effect", "coverflowRotate", 50),
      stretch: get(config, "effect", "coverflowStretch", 0),
      depth: get(config, "effect", "coverflowDepth", 100),
      modifier: get(config, "effect", "coverflowModifier", 1),
      slideShadows,
    };
  }
  if (effect === "cards") {
    use(M.EffectCards);
    props.cardsEffect = {
      rotate: get(config, "effect", "cardsRotate", true) === true,
      perSlideOffset: get(config, "effect", "cardsPerSlideOffset", 8),
      perSlideRotate: get(config, "effect", "cardsPerSlideRotate", 2),
      slideShadows,
    };
  }
  if (effect === "creative") {
    use(M.EffectCreative);
    props.creativeEffect = CREATIVE_PRESETS[get(config, "effect", "creativePreset", "slide")] || CREATIVE_PRESETS.slide;
  }

  // Layout
  let slidesPerView = get(config, "layout", "slidesPerView", 1);
  let spaceBetween = get(config, "layout", "spaceBetween", 0);
  let slidesPerGroup = get(config, "layout", "slidesPerGroup", 1);
  if (singleSlide) { slidesPerView = 1; slidesPerGroup = 1; }
  if (gridRows > 1 && (slidesPerView === "auto" || slidesPerView === LEGACY)) slidesPerView = slidesPerView === LEGACY ? LEGACY : 1;
  const useLegacy = !preview && legacyTable && (slidesPerView === LEGACY || spaceBetween === LEGACY);
  if (useLegacy) {
    const base = legacyTable[0] || legacyTable["0"] || {};
    props.slidesPerView = slidesPerView === LEGACY ? base.slidesPerView : slidesPerView;
    props.spaceBetween = spaceBetween === LEGACY ? base.spaceBetween : spaceBetween;
    const breakpoints = legacyBreakpoints(legacyTable, { slidesPerView, spaceBetween });
    if (breakpoints) props.breakpoints = breakpoints;
  } else {
    // Preview (or no legacy table): the legacy value is read at the device's
    // representative width, exactly what that device would show.
    const pick = (value, key) => {
      if (value !== LEGACY) return value;
      if (!legacyTable) return key === "slidesPerView" ? 1 : 0;
      const width = SCHEMA.devices[device]?.previewWidth || 1280;
      const entry = Object.entries(legacyTable)
        .filter(([bp]) => Number(bp) <= width)
        .sort((a, b) => Number(b[0]) - Number(a[0]))[0]?.[1] || {};
      return entry[key] ?? (key === "slidesPerView" ? 1 : 0);
    };
    props.slidesPerView = pick(slidesPerView, "slidesPerView");
    props.spaceBetween = pick(spaceBetween, "spaceBetween");
  }
  props.slidesPerGroup = slidesPerGroup;
  // swiperPageMatrix's real movement rule. Kept central so every managed
  // carousel gets the same first-step behaviour unless Swiper Settings adds
  // a future explicit option for it.
  props.slidesPerGroupSkip = 1;
  if (gridRows > 1) {
    use(M.Grid);
    props.grid = { rows: gridRows, fill: get(config, "layout", "gridFill", "row") };
  }
  const vertical = direction === "vertical";
  props.autoHeight = !vertical && get(config, "layout", "autoHeight", false) === true;
  const width = get(config, "layout", "width", null);
  const maxWidth = get(config, "layout", "maxWidth", 0);
  let height = get(config, "layout", "height", 0);
  if (vertical && !height && has("effect", "direction")) height = 360; // a vertical Swiper needs a height
  const aspectRatio = get(config, "layout", "aspectRatio", "auto");
  if (width !== null && width !== undefined && has("layout", "width")) { style.width = `${width}%`; if (width < 100) { style.marginLeft = "auto"; style.marginRight = "auto"; } }
  if (maxWidth > 0) style.maxWidth = px(maxWidth);
  if (height > 0) style.height = px(height);
  if (aspectRatio !== "auto" && has("layout", "aspectRatio")) style.aspectRatio = aspectRatio;
  if (has("layout", "overflow")) style.overflow = get(config, "layout", "overflow", "hidden");

  // Appearance (only what was configured -- the origin never sets these)
  if (has("appearance", "backgroundColor")) style.backgroundColor = config.appearance.backgroundColor;
  if (has("appearance", "borderRadius")) style.borderRadius = px(config.appearance.borderRadius);
  const padding = get(config, "appearance", "padding", null);
  if (padding !== null && has("appearance", "padding")) style.padding = px(padding);

  // Navigation
  const navSetting = get(config, "navigation", "enabled", false);
  const navEnabled = navSetting === "auto" ? Boolean(autoNavigation) : navSetting === true;
  if (navEnabled) {
    use(M.Navigation);
    props.navigation = true;
    const nav = config.navigation || {};
    const navVar = (fieldKey, varName, value, cls) => {
      if (nav[fieldKey] === undefined) return;
      cssVars[varName] = value;
      classes.push(cls);
    };
    navVar("size", "--elk-nav-size", px(nav.size), "elk-nav-size");
    navVar("iconSize", "--elk-nav-icon", px(nav.iconSize), "elk-nav-icon");
    navVar("color", "--elk-nav-color", nav.color, "elk-nav-color");
    navVar("hoverColor", "--elk-nav-hover-color", nav.hoverColor, "elk-nav-hover-color");
    navVar("background", "--elk-nav-bg", nav.background, "elk-nav-bg");
    navVar("hoverBackground", "--elk-nav-hover-bg", nav.hoverBackground, "elk-nav-hover-bg");
    navVar("borderRadius", "--elk-nav-radius", px(nav.borderRadius), "elk-nav-radius");
    navVar("shadow", "--elk-nav-shadow", SHADOWS[nav.shadow] || "none", "elk-nav-shadow");
    if (nav.borderColor !== undefined || nav.borderWidth !== undefined) {
      cssVars["--elk-nav-bw"] = px(nav.borderWidth ?? 1);
      cssVars["--elk-nav-bc"] = nav.borderColor || "currentColor";
      classes.push("elk-nav-border");
    }
    if (nav.offset !== undefined) { cssVars["--elk-nav-offset"] = px(nav.offset); classes.push("elk-nav-offset"); }
    if (nav.position === "outside" && config.navigation?.position !== undefined) {
      const size = nav.size ?? 44;
      cssVars["--elk-nav-gutter"] = px(size + (nav.offset ?? 8));
      classes.push("elk-nav-outside");
    }
    if (nav.showOnHover === true) classes.push("elk-nav-hover-only");
  } else {
    props.navigation = false;
  }

  // Pagination
  if (get(config, "pagination", "enabled", false) === true) {
    use(M.Pagination);
    const pag = config.pagination || {};
    const type = pag.type || "bullets";
    props.pagination = type === "bullets"
      ? { type, clickable: pag.clickable !== false, dynamicBullets: pag.dynamicBullets === true }
      : { type };
    const pagVar = (fieldKey, varName, value, cls) => {
      if (pag[fieldKey] === undefined) return;
      cssVars[varName] = value;
      classes.push(cls);
    };
    pagVar("bulletSize", "--elk-pag-size", px(pag.bulletSize), "elk-pag-size");
    pagVar("color", "--elk-pag-color", pag.color, "elk-pag-color");
    pagVar("activeColor", "--elk-pag-active", pag.activeColor, "elk-pag-active");
    pagVar("inactiveOpacity", "--elk-pag-opacity", String(pag.inactiveOpacity), "elk-pag-opacity");
    pagVar("gap", "--elk-pag-gap", px(pag.gap), "elk-pag-gap");
    pagVar("offset", "--elk-pag-offset", px(pag.offset), "elk-pag-offset");
    pagVar("progressbarSize", "--elk-pag-bar", px(pag.progressbarSize), "elk-pag-bar");
    if (pag.bulletShape && pag.bulletShape !== "circle") classes.push(`elk-pag-${pag.bulletShape}`);
    if (pag.position === "top") classes.push("elk-pag-top");
    else if (type === "progressbar" && pag.position === "bottom") classes.push("elk-pag-bar-bottom");
  } else {
    props.pagination = false;
  }

  // Scrollbar
  if (get(config, "scrollbar", "enabled", false) === true) {
    use(M.Scrollbar);
    const sb = config.scrollbar || {};
    props.scrollbar = { draggable: sb.draggable === true, hide: sb.hide === true, snapOnRelease: sb.snapOnRelease !== false };
    const sbVar = (fieldKey, varName, value, cls) => {
      if (sb[fieldKey] === undefined) return;
      cssVars[varName] = value;
      classes.push(cls);
    };
    sbVar("thickness", "--elk-sb-size", px(sb.thickness), "elk-sb-size");
    sbVar("length", "--elk-sb-length", `${sb.length}%`, "elk-sb-length");
    if (sb.length !== undefined) cssVars["--elk-sb-inset"] = `${(100 - sb.length) / 2}%`;
    sbVar("trackColor", "--elk-sb-track", sb.trackColor, "elk-sb-track");
    sbVar("thumbColor", "--elk-sb-thumb", sb.thumbColor, "elk-sb-thumb");
    sbVar("opacity", "--elk-sb-opacity", String(sb.opacity), "elk-sb-opacity");
    sbVar("borderRadius", "--elk-sb-radius", px(sb.borderRadius), "elk-sb-radius");
    sbVar("offset", "--elk-sb-offset", px(sb.offset), "elk-sb-offset");
    if (sb.position === "top") classes.push("elk-sb-top");
  } else {
    props.scrollbar = false;
  }

  // Autoplay (in preview too: the preview reflects the edited config)
  if (get(config, "autoplay", "enabled", false) === true) {
    use(M.Autoplay);
    const ap = config.autoplay || {};
    props.autoplay = {
      delay: ap.delay ?? 3000,
      reverseDirection: ap.reverseDirection === true,
      pauseOnMouseEnter: ap.pauseOnMouseEnter === true,
      disableOnInteraction: ap.disableOnInteraction === true,
      stopOnLastSlide: ap.stopOnLastSlide === true,
    };
  } else {
    props.autoplay = false;
  }

  // Keyboard / mousewheel
  if (get(config, "keyboard", "enabled", false) === true) {
    use(M.Keyboard);
    props.keyboard = { enabled: true, onlyInViewport: get(config, "keyboard", "onlyInViewport", true) !== false, pageUpDown: get(config, "keyboard", "pageUpDown", true) !== false };
  } else {
    props.keyboard = false;
  }
  if (get(config, "mousewheel", "enabled", false) === true) {
    use(M.Mousewheel);
    props.mousewheel = {
      sensitivity: get(config, "mousewheel", "sensitivity", 1),
      invert: get(config, "mousewheel", "invert", false) === true,
      forceToAxis: get(config, "mousewheel", "forceToAxis", false) === true,
      releaseOnEdges: get(config, "mousewheel", "releaseOnEdges", false) === true,
    };
  } else {
    props.mousewheel = false;
  }

  // Touch
  props.allowTouchMove = get(config, "touch", "allowTouchMove", true) !== false;
  if (has("touch", "touchRatio")) props.touchRatio = config.touch.touchRatio;
  if (has("touch", "touchAngle")) props.touchAngle = config.touch.touchAngle;
  if (has("touch", "resistance")) props.resistance = config.touch.resistance;
  if (has("touch", "resistanceRatio")) props.resistanceRatio = config.touch.resistanceRatio;
  if (has("touch", "followFinger")) props.followFinger = config.touch.followFinger;
  props.simulateTouch = get(config, "touch", "simulateTouch", true) !== false;
  if (has("touch", "shortSwipes")) props.shortSwipes = config.touch.shortSwipes;

  // Advanced
  const freeMode = get(config, "advanced", "freeMode", false) === true;
  if (freeMode) { use(M.FreeMode); props.freeMode = { enabled: true, momentum: get(config, "advanced", "freeModeMomentum", true) !== false }; }
  // CSS Scroll Snap mode cannot run 3D/fade effects nor the Grid: they win.
  props.cssMode = get(config, "advanced", "cssMode", false) === true && effect === "slide" && gridRows <= 1 && !freeMode;
  props.watchOverflow = get(config, "advanced", "watchOverflow", true) !== false;
  if (get(config, "advanced", "a11y", false) === true) use(M.A11y);
  if (has("advanced", "slideToClickedSlide")) props.slideToClickedSlide = config.advanced.slideToClickedSlide;

  props.modules = modules;
  const className = classes.join(" ");
  // Structural options cannot be hot-swapped on a live Swiper instance:
  // a change re-creates it cleanly (React key).
  const structuralKey = [
    device, effect, direction, props.loop, props.rewind, gridRows, props.cssMode, props.centeredSlides,
    navEnabled, props.pagination && props.pagination.type, props.pagination && props.pagination.dynamicBullets,
    Boolean(props.scrollbar), Boolean(props.autoplay), freeMode, Boolean(props.keyboard), Boolean(props.mousewheel),
    props.slidesPerView, props.slidesPerGroup, props.spaceBetween, JSON.stringify(props.breakpoints || null),
  ].join("|");
  return { props, modules, style: { ...style, ...cssVars }, className, structuralKey };
};

// One-call helper used by useElakaySwiper and the Settings preview.
// `originPatch` (optional): origin values the component computes at runtime
// (e.g. dynamic bullets only past 8 slides) -- still level 3 (origin).
export const computeSwiper = ({ componentId, device, defaults, instance, legacyTable, legacyExplicit, autoNavigation, modules, slideCount, preview, originPatch }) => {
  const component = COMPONENT_BY_ID[componentId] || null;
  const origin = originFor(component, device);
  if (isPlainObject(originPatch)) {
    Object.entries(originPatch).forEach(([groupKey, group]) => {
      if (isPlainObject(group)) origin[groupKey] = { ...(origin[groupKey] || {}), ...group };
    });
  }
  if (component?.legacyResponsive && legacyTable) {
    origin.layout = { ...(origin.layout || {}), slidesPerView: LEGACY, spaceBetween: LEGACY };
  }
  const cleanDefaults = sanitizeDeviceConfig(defaults?.[device], component);
  const cleanInstance = sanitizeDeviceConfig(instance?.[device], component);
  if (legacyExplicit && legacyTable && cleanInstance.layout?.slidesPerView === undefined) {
    cleanInstance.layout = { ...(cleanInstance.layout || {}), slidesPerView: LEGACY };
  }
  const resolved = resolveDeviceConfig({ origin, defaults: cleanDefaults, instance: cleanInstance });
  return { resolved, ...buildSwiperProps(resolved, { modules, device, legacyTable, autoNavigation, slideCount, preview }) };
};

// Legacy props (wm0 / wm640 / wm768 / wm1024 / wm1200 [/ wm1440]) -> table.
export const legacyTableFrom = ({ wm0, wm640, wm768, wm1024, wm1200, wm1440 }, spacing = {}) => {
  const table = {
    0: { slidesPerView: wm0, spaceBetween: spacing[0] ?? 10 },
    568: { slidesPerView: wm640, spaceBetween: spacing[568] ?? spacing[0] ?? 10 },
    768: { slidesPerView: wm768, spaceBetween: spacing[768] ?? spacing[568] ?? spacing[0] ?? 10 },
    1024: { slidesPerView: wm1024, spaceBetween: spacing[1024] ?? spacing[768] ?? 10 },
    1200: { slidesPerView: wm1200, spaceBetween: spacing[1200] ?? spacing[1024] ?? 10 },
  };
  if (wm1440 !== undefined) table[1440] = { slidesPerView: wm1440, spaceBetween: spacing[1440] ?? spacing[1200] ?? 10 };
  return table;
};
